/* helper.ts */

/* d3.js */
import * as d3 from "d3";

/* Utils */
import { capitalizeFirstLetter } from "src/utils/helper/string-transformation";

/* Styles */
import { basicStyles } from "src/shared/styles/Charts";

/* Interfaces */
import { SurvivalPlotTooltipProps } from "src/shared/interfaces/ChartTooltip";

export const createXScale = (
  allXValues: number[],
  plotWidth: number
): d3.ScaleLinear<number, number> => {
  return d3
    .scaleLinear()
    .domain([0, d3.max(allXValues)!])
    .nice(10)
    .range([0, plotWidth]);
};

export const createYScaleSurvival = (
  plotHeight: number
) => {
  return d3
    .scaleLinear()
    .domain([0, 1])
    .nice()
    .range([plotHeight, 0]);
};

export const setTooltipData = (
  tooltipData: SurvivalPlotTooltipProps
) => {
  if (!tooltipData) return [];

  const { label, color, time, event } = tooltipData;

  return [
    {
      color: color,
      value: capitalizeFirstLetter(label),
    },
    { key: "Time", value: time },
    { key: "Event", value: event },
  ];
};

export const drawAxes = (
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  tickValues: number[],
  plotHeight: number,
  plotWidth: number
) => {
  // x-Axis
  const xAxis = g
    .append("g")
    .attr("transform", `translate(0,${plotHeight})`)
    .call(
      d3
        .axisBottom(xScale)
        .tickValues(tickValues ?? xScale.ticks(10))
        .tickSize(0)
    )
    .call((g) => g.select(".domain").remove());

  xAxis
    .selectAll("text")
    .attr("y", 21)
    .style("font-size", basicStyles.font.size)
    .style("fill", basicStyles.font.color);

  // y-Axis
  const yAxis = g
    .append("g")
    .call(
      d3
        .axisLeft(yScale)
        .ticks(5)
        .tickSize(-plotWidth)
        .tickPadding(21)
    )
    .call((g) => g.select(".domain").remove());

  yAxis
    .selectAll("text")
    .style("font-size", basicStyles.font.size)
    .style("fill", basicStyles.font.color);

  yAxis
    .selectAll(".tick line")
    .attr("stroke", (d) =>
      d === 0
        ? basicStyles.grid.xAxis.zeroLineColor
        : "#ccc"
    )
    .attr("stroke-width", (d) => (d === 0 ? 1.5 : 1))
    .attr("stroke-dasharray", (d) =>
      d === 0 ? "none" : "3,5"
    );

  // x-Axis (label)
  g.append("text")
    .attr("text-anchor", "middle")
    .attr("x", plotWidth / 2)
    .attr("y", plotHeight + 62)
    .style("font-size", basicStyles.font.size)
    .style("fill", basicStyles.font.color)
    .text("Time (in mths)");

  // y-Axis (label)
  g.append("text")
    .attr("text-anchor", "middle")
    .attr("transform", "rotate(-90)")
    .attr("x", -plotHeight / 2)
    .attr("y", -62)
    .style("font-size", basicStyles.fontSize.unit)
    .style("fill", basicStyles.font.color)
    .text("Propability (%)");
};
